import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ApiError } from "../api/types";
import { useAuth } from "../store/auth";
import { useChat } from "../store/chat";
import { useMembers } from "../store/members";
import { useLocale } from "../hooks/useLocale";
import { AppShell } from "../components/AppShell";
import { Sidebar } from "../components/Sidebar";
import styles from "./MembersPage.module.css";

export function MembersPage() {
  const { t, tOr } = useLocale();
  const navigate = useNavigate();

  const currentUser = useAuth((s) => s.currentUser);
  const tenant = useAuth((s) => s.tenant);
  const renameWorkspace = useAuth((s) => s.renameWorkspace);
  const members = useMembers((s) => s.list);
  const loadMembers = useMembers((s) => s.loadList);
  const invite = useMembers((s) => s.invite);
  const remove = useMembers((s) => s.remove);
  const changeRole = useMembers((s) => s.changeRole);

  const createChat = useChat((s) => s.createSession);

  const [email, setEmail] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  const [busy, setBusy] = useState(false);
  const [renameBusy, setRenameBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [renameNotice, setRenameNotice] = useState<string | null>(null);

  const onSidebarCreate = useCallback(async () => {
    const chat = await createChat();
    navigate(`/chat/${chat.id}`);
  }, [createChat, navigate]);

  useEffect(() => {
    if (currentUser?.role === "owner") void loadMembers();
  }, [currentUser?.role, loadMembers]);

  useEffect(() => {
    setWorkspaceName(tenant?.name ?? "");
  }, [tenant?.name]);

  // Members can't manage the workspace — bounce them back to the app. (The
  // backend also rejects the underlying calls, this just avoids a dead screen.)
  if (currentUser != null && currentUser.role !== "owner") {
    return <Navigate to="/chat" replace />;
  }

  async function onRename(e: FormEvent) {
    e.preventDefault();
    if (renameBusy) return;
    const trimmed = workspaceName.trim();
    if (!trimmed || trimmed === tenant?.name) return;
    setRenameBusy(true);
    setRenameError(null);
    setRenameNotice(null);
    try {
      await renameWorkspace(trimmed);
      setRenameNotice(t("workspace.rename_saved"));
    } catch (err) {
      setRenameError(workspaceError(err));
    } finally {
      setRenameBusy(false);
    }
  }

  function workspaceError(err: unknown): string {
    if (err instanceof ApiError) {
      if (err.code === "forbidden") {
        return t("workspace.rename_forbidden");
      }
      return tOr(`workspace.error.${err.code}`, "workspace.error.unknown");
    }
    return t("workspace.error.unknown");
  }

  async function onInvite(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    const trimmed = email.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await invite(trimmed);
      setEmail("");
      setNotice(t("members.invite_sent", { email: trimmed }));
    } catch (err) {
      setError(memberError(err));
    } finally {
      setBusy(false);
    }
  }

  async function onRemove(id: number, label: string) {
    if (!window.confirm(t("members.remove_confirm", { email: label }))) return;
    setError(null);
    setNotice(null);
    try {
      await remove(id);
    } catch (err) {
      setError(memberError(err));
    }
  }

  async function onToggleRole(id: number, current: "owner" | "member") {
    setError(null);
    setNotice(null);
    const next = current === "owner" ? "member" : "owner";
    try {
      await changeRole(id, next);
    } catch (err) {
      setError(memberError(err));
    }
  }

  function memberError(err: unknown): string {
    if (err instanceof ApiError) {
      return tOr(`members.error.${err.code}`, "members.error.unknown");
    }
    return t("members.error.unknown");
  }

  return (
    <AppShell sidebar={<Sidebar onCreate={() => void onSidebarCreate()} />}>
      <div className={styles.page}>
        <header className={styles.header}>
          <h2 className={styles.title}>{t("members.title")}</h2>
          <p className={styles.subtitle}>{t("members.subtitle")}</p>
        </header>

        <section className={styles.renameSection}>
          <h3 className={styles.renameTitle}>{t("workspace.rename_title")}</h3>
          <form className={styles.renameForm} onSubmit={onRename}>
            <input
              type="text"
              className={styles.input}
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              maxLength={255}
              required
            />
            <button
              type="submit"
              className={styles.inviteBtn}
              disabled={renameBusy || workspaceName.trim() === tenant?.name}
            >
              {renameBusy ? t("common.loading") : t("workspace.rename_submit")}
            </button>
          </form>
          {renameError && <p className={styles.error}>{renameError}</p>}
          {renameNotice && <p className={styles.notice}>{renameNotice}</p>}
        </section>

        <form className={styles.inviteForm} onSubmit={onInvite}>
          <input
            type="email"
            className={styles.input}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("members.invite_placeholder")}
            autoComplete="off"
            required
          />
          <button type="submit" className={styles.inviteBtn} disabled={busy}>
            {busy ? t("common.loading") : t("members.invite_submit")}
          </button>
        </form>

        {error && <p className={styles.error}>{error}</p>}
        {notice && <p className={styles.notice}>{notice}</p>}

        <ul className={styles.list}>
          {members.map((m) => {
            const isSelf = m.id === currentUser?.id;
            return (
              <li key={m.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <span className={styles.email}>{m.email}</span>
                  <span className={styles.roleTag}>
                    {m.role === "owner" ? t("members.role_owner") : t("members.role_member")}
                  </span>
                  {!m.email_confirmed && (
                    <span className={styles.pending}>{t("members.pending")}</span>
                  )}
                </div>
                <div className={styles.rowActions}>
                  <button
                    type="button"
                    className={styles.actionBtn}
                    onClick={() => void onToggleRole(m.id, m.role)}
                  >
                    {m.role === "owner"
                      ? t("members.demote")
                      : t("members.promote")}
                  </button>
                  {!isSelf && (
                    <button
                      type="button"
                      className={`${styles.actionBtn} ${styles.dangerBtn}`}
                      onClick={() => void onRemove(m.id, m.email)}
                    >
                      {t("members.remove")}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </AppShell>
  );
}
